import { useEffect, useRef, useCallback } from 'react'

const API = import.meta.env.VITE_API_URL || '/api'

// Identidad estable de ESTE dispositivo — la usa el servidor para el candado de
// "una sola medición en curso por cuenta" y para que otro dispositivo sepa
// desde dónde se está transmitiendo. Se cachea en memoria: aunque el storage
// del teléfono falle, la identidad NUNCA cambia dentro de una misma sesión
// (si cambiara, el dispositivo se bloquearía a sí mismo con el candado).
let _dev = null
export function deviceIdentity() {
  if (_dev) return _dev
  let id = null
  try {
    id = localStorage.getItem('vc_device_id')
    if (!id) {
      id = 'dev-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
      localStorage.setItem('vc_device_id', id)
    }
  } catch { /* sin storage: identidad de esta sesión nomás */ }
  if (!id) id = 'dev-' + Math.random().toString(36).slice(2, 10)
  _dev = { id, label: (window.__MANABI_SHELL__ || window.__VITALCORE_SHELL__) ? 'la app del teléfono' : 'el navegador' }
  return _dev
}

export function useLiveStream(token, hr, status, rmssd, ecgRef, onViewers, onBlocked, sensorMeta, onMessages) {
  const ref = useRef({ token, hr, status, rmssd, ecgRef, onViewers, onBlocked, sensorMeta, onMessages })
  ref.current = { token, hr, status, rmssd, ecgRef, onViewers, onBlocked, sensorMeta, onMessages }
  const lastEcgLen   = useRef(0)
  const wasLive        = useRef(false)
  const sessionStart   = useRef(null)
  const lastSent       = useRef(0)
  const blockedUntil   = useRef(0)

  const send = useCallback(() => {
    const { token, hr, status, rmssd, ecgRef, sensorMeta } = ref.current
    if (!token) return
    const now = Date.now()
    if (now < blockedUntil.current) return       // otro dispositivo transmite: no insistir
    if (now - lastSent.current < 450) return     // tope de frecuencia (evento + intervalo)
    lastSent.current = now
    // Only 'connected' counts as live — 'reconnecting' sends null to avoid stale values
    const isLive = !!(hr && status === 'connected')

    // Avoid spamming the server with offline status if we were already offline
    if (!isLive && !wasLive.current) return

    // A new live session just started — re-enable retransmission to the live page.
    if (isLive && !wasLive.current) {
      sessionStart.current = now
      fetch(`${API}/live/resume`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
    } else if (!isLive) {
      sessionStart.current = null
    }
    wasLive.current = isLive

    // Delta ECG: only new samples since last send. Con tope: si la pestaña
    // estuvo dormida un rato largo, el delta acumulado puede pesar cientos de
    // KB y el servidor lo rechaza — se manda solo los últimos ~20 s (2600
    // muestras a 130 Hz) y el resto se salta (para el vivo alcanza y sobra).
    const buf = ecgRef?.current ?? []
    if (buf.length < lastEcgLen.current) lastEcgLen.current = 0  // buffer was reset
    const ecgDelta = buf.slice(Math.max(lastEcgLen.current, buf.length - 2600))
    lastEcgLen.current = buf.length

    const dev = deviceIdentity()
    fetch(`${API}/live/update`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body:    JSON.stringify({
        hr:          isLive ? hr     : null,
        status:      isLive ? status : 'offline',
        rmssd:       isLive ? rmssd  : null,
        startTime:   sessionStart.current,
        ecg:         ecgDelta.length ? ecgDelta : undefined,
        deviceId:    dev.id,
        deviceLabel: dev.label,
        deviceName:  isLive ? (sensorMeta?.deviceName || 'Polar H10') : null,
        sensorId:    isLive ? sensorMeta?.sensorId : null,
        battery:     isLive ? sensorMeta?.battery  : null,
        steps:       isLive ? (sensorMeta?.steps ?? 0) : null,
        moisture:    isLive ? sensorMeta?.moisture : null,
        posture:     isLive ? sensorMeta?.posture  : null,
      }),
    })
      .then(async r => {
        if (r.status === 409) {
          // Otro dispositivo de la cuenta ya está midiendo: freno 15 s y aviso
          const d = await r.json().catch(() => ({}))
          blockedUntil.current = Date.now() + 15_000
          ref.current.onBlocked?.(d.otherDevice || 'otro dispositivo')
          return null
        }
        return r.ok ? r.json() : null
      })
      .then(d => {
        if (!d) return
        if (ref.current.onViewers) ref.current.onViewers(d.viewers ?? 0)
        if (ref.current.onMessages && d.messages !== undefined) {
          ref.current.onMessages(d.messages || [], d.pinnedMessage || null)
        }
      })
      .catch(() => {})
  }, [])

  // Latido de respaldo cada 500 ms (pestaña visible / navegador de escritorio)
  useEffect(() => {
    send()
    const id = setInterval(send, 500)
    return () => clearInterval(id)
  }, [send])

  // Envío por EVENTO además del intervalo: con la pantalla bloqueada Android
  // frena los intervalos del WebView, pero cada latido del sensor re-renderiza
  // y pasa por acá → la retransmisión sigue viva igual.
  useEffect(() => { send() }, [hr, status, send])
}
